import { createStore, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import BaseComponent from 'foundation/base-component';
import reducer from './reducer';
import PlayerRegistrationStep1 from './step1';
import PlayerRegistrationStep2 from './step2';
import PlayerRegistrationStep3 from './step3';
import PlayerRegistrationStep4 from './step4';
import PlayerRegistrationNavigation from './navigation';
import FormComponent from '../../form/js/form.component';
import creators from './actions';
import app from '../../../general/js/app';

export default class PlayerRegistrationComponent extends BaseComponent {
    constructor(...args) {
        super(...args);

        this.currentStep = null;
        this.steps = {};
        this.onNavigate = this.onNavigate.bind(this);
        this.onContinue = this.onContinue.bind(this);
        this.onChangeStore = this.onChangeStore.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onServerValidationError = this.onServerValidationError.bind(this);
        this.init();
    }

    static getRequiredRefs() {
        return ['nav', 'form', 'description', 'notification'];
    }

    init() {
        // add listener to form first in order to be able to prevent event propagation in some cases
        this.refs.form.addEventListener('submit', this.onSubmit);

        // init inner components
        this.formComponent = new FormComponent(this.refs.form, 'form', { onServerValidationError: this.onServerValidationError });
        this.parsleyFormInstance = this.formComponent.getParsleyForm();
        this.store = createStore(reducer, applyMiddleware(thunk));
        (new PlayerRegistrationNavigation(this.refs.nav, 'navigation')).init({
            store: this.store,
            onNavigate: this.onNavigate
        });

        const stepParams = {
            store: this.store,
            onContinue: this.onContinue
        };

        if (this.refs.step1) {
            const stepInstance = new PlayerRegistrationStep1(this.refs.step1, 'step1');
            this.steps[stepInstance.getStepNumber()] = stepInstance;
            stepInstance.init(stepParams);
        }
        if (this.refs.step2) {
            const stepInstance = new PlayerRegistrationStep2(this.refs.step2, 'step2');
            this.steps[stepInstance.getStepNumber()] = stepInstance;
            stepInstance.init(stepParams);
        }
        if (this.refs.step3) {
            const stepInstance = new PlayerRegistrationStep3(this.refs.step3, 'step3');
            this.steps[stepInstance.getStepNumber()] = stepInstance;
            stepInstance.init(stepParams);
        }
        if (this.refs.step4) {
            const stepInstance = new PlayerRegistrationStep4(this.refs.step4, 'step4');
            this.steps[stepInstance.getStepNumber()] = stepInstance;
            stepInstance.init(stepParams);
        }

        this.addListeners();
    }

    onSubmit(e) {
        // submit is allowed only from the last step
        // in other cases, e.g user clicks on Enter must not trigger submit
        if (!this.currentStep || this.currentStep !== Object.keys(this.steps).length) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.continue();
        }
    }

    onServerValidationError(failedElements) {
        if (failedElements.length > 0) {
            const firstFailedElement = failedElements[0];
            Object.keys(this.steps).forEach((stepNumber) => {
                const stepInstance = this.steps[stepNumber];
                if (stepInstance.element.contains(firstFailedElement)) {
                    this.store.dispatch(creators.changeStep(stepInstance.getStepNumber()));
                }
            });
        }
    }

    addListeners() {
        this.store.subscribe(this.onChangeStore);
    }

    onChangeStore() {
        this.applyState(this.store.getState());
    }

    applyState({ step }) {
        if (this.currentStep !== step) {
            this.applyNewStep(step);
        }
    }

    applyNewStep(step) {
        this.currentStep = step;
        // less then tablet
        if (!app.isTablet) {
            if (this.currentStep === 1) {
                this.refs.description.style.display = 'block';
                this.refs.notification.style.display = 'block';
            } else {
                this.refs.description.style.display = 'none';
                this.refs.notification.style.display = 'none';
            }
        }
    }

    onContinue() {
        this.continue();
    }

    continue() {
        const currentStep = this.store.getState().step;
        const nextStep = currentStep + 1;
        this.canGoToStep(nextStep, true).then(() => {
            this.store.dispatch(creators.changeStep(nextStep));
        });
    }

    onNavigate(newStep) {
        this.canGoToStep(newStep).then(() => {
            this.store.dispatch(creators.changeStep(newStep));
        });
    }

    canGoToStep(nextStep, highlight = false) {
        const currentStep = this.store.getState().step;
        const promises = [this.validFormStep(nextStep, highlight)];

        const currentStepInstance = this.steps[currentStep];
        if (currentStepInstance) {
            promises.push(currentStepInstance.getIsStepValidPromise());
        }
        return Promise.all(promises);
    }

    validFormStep(newStep, highlight = false) {
        const state = this.store.getState();
        const previousSteps = state.steps.filter((step) => step < newStep);
        if (previousSteps.length > 0) {
            const promises = [];
            previousSteps.forEach((step) => {
                let stepPromise;
                if (highlight) {
                    stepPromise = this.parsleyFormInstance.whenValidate({ group: step });
                } else {
                    stepPromise = this.parsleyFormInstance.whenValid({ group: step });
                }
                promises.push(stepPromise);
            });
            return Promise.all(promises);
        }

        return Promise.resolve();
    }
}
